import { useEffect } from 'react'
import type { RefObject } from 'react'
import { hasActiveSubscription } from 'Model/User/IUser.js'
import type { IUser } from 'Model/User/IUser.js'
import { tracingAxios } from 'Repository/Sentry.js'
import { Environment } from 'environment.js'
import type { CreditBalanceDTO } from '../../../Generated/CreditBalanceDTO.ts'

interface UseRechargePollingOptions {
  user: IUser | undefined
  awaitingSync: boolean
  rechargePending: boolean
  balance: CreditBalanceDTO | null
  setAwaitingSync: (value: boolean) => void
  setRechargePending: (value: boolean) => void
  reload: () => void
  rechargeInitialBalance: RefObject<number | null>
}

export function useRechargePolling({
  user,
  awaitingSync,
  rechargePending,
  balance,
  setAwaitingSync,
  setRechargePending,
  reload,
  rechargeInitialBalance
}: UseRechargePollingOptions): void {
  // Poll refresh-session to force token refresh until subscription appears
  useEffect(() => {
    if (!awaitingSync) return

    const interval = setInterval(async () => {
      try {
        await tracingAxios.get(`${Environment.API_URL}/v1/chargebee/refresh-session`, { withCredentials: true })
      } catch (e) {
        console.warn('Subscription sync poll failed', e)
      }
    }, 3000)

    const timeout = setTimeout(() => setAwaitingSync(false), 30000)

    return () => {
      clearInterval(interval)
      clearTimeout(timeout)
    }
  }, [awaitingSync])

  // Stop polling once subscription data arrives via refreshed token
  useEffect(() => {
    if (awaitingSync && user && hasActiveSubscription(user.org)) {
      setAwaitingSync(false)
    }
  }, [awaitingSync, user?.org.chargebeePlanId, user?.org.chargebeeSubscriptionStatus])

  // After a recharge checkout, periodically reload credits until the balance changes.
  useEffect(() => {
    if (!rechargePending) return
    const interval = setInterval(() => reload(), 3000)
    return () => clearInterval(interval)
  }, [rechargePending, reload])

  // Once the reloaded balance differs from the pre-recharge value, the purchase has landed.
  useEffect(() => {
    if (rechargePending && balance && balance.balance !== rechargeInitialBalance.current) {
      setRechargePending(false)
    }
  }, [rechargePending, balance])
}
